import type { SessionTokens } from "@amazing-care/auth";
import { ROUTES } from "@/routes";
import teamsApps from "./teams-apps.json";

/**
 * The portal inside Microsoft Teams. The Teams app's tab URL carries
 * `?host=teams`; the flag is kept for the tab's lifetime because the first
 * redirect (to /login, or to a document) drops the query string.
 */

const HOST_PARAM = "host";
const TEAMS_HOST = "teams";
const STORAGE_KEY = "amazing-care:host";

// Microsoft's sign-in popup size; Teams sizes the window from these.
const SIGN_IN_POPUP = { width: 600, height: 535 };

function readStoredHost(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Remembers that this tab was opened by Teams. Call once, before routing. */
export function rememberTeamsHost(search = window.location.search): void {
  if (new URLSearchParams(search).get(HOST_PARAM) !== TEAMS_HOST) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, TEAMS_HOST);
  } catch {
    // Storage can be blocked; the tab then behaves as a browser page.
  }
}

export function isInTeams(): boolean {
  return readStoredHost() === TEAMS_HOST;
}

function forgetTeamsHost(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: nothing was remembered.
  }
}

type TeamsSdk = typeof import("@microsoft/teams-js");

let sdk: Promise<TeamsSdk> | undefined;

/**
 * The Teams SDK, initialized. Imported on first use so a browser visit never
 * downloads it.
 */
export function loadTeams(): Promise<TeamsSdk> {
  sdk ??= import("@microsoft/teams-js")
    .then(async (teams) => {
      await teams.app.initialize();
      return teams;
    })
    .catch((error: unknown) => {
      // Let the next call try again rather than keep a failed start forever.
      sdk = undefined;
      throw error;
    });
  return sdk;
}

/**
 * Whether Teams answers this tab. `?host=teams` is only a URL parameter, so a
 * shared link carries it into a plain browser tab; there the SDK fails and the
 * tab goes back to behaving as a browser page.
 */
export async function reachTeams(): Promise<boolean> {
  if (!isInTeams()) return false;
  try {
    await loadTeams();
    return true;
  } catch {
    forgetTeamsHost();
    return false;
  }
}

function parseTokens(result: string): SessionTokens {
  const value: unknown = JSON.parse(result);
  if (
    typeof value === "object" &&
    value !== null &&
    "access_token" in value &&
    "refresh_token" in value &&
    typeof value.access_token === "string" &&
    typeof value.refresh_token === "string"
  ) {
    return { access_token: value.access_token, refresh_token: value.refresh_token };
  }
  throw new Error("The Teams sign-in returned no session.");
}

/**
 * Microsoft's sign-in page refuses to load in a frame, and a Teams tab is one,
 * so sign-in runs in a Teams popup on the portal's own origin and hands the
 * session back as tokens.
 */
export async function signInThroughTeams(): Promise<SessionTokens> {
  const teams = await loadTeams();
  const result = await teams.authentication.authenticate({
    url: new URL(ROUTES.teamsSignInStart, window.location.origin).toString(),
    ...SIGN_IN_POPUP,
  });
  return parseTokens(result);
}

const OWN_APP_IDS = new Set(Object.values(teamsApps).map((app) => app.id));

/**
 * Called in the popup once it is signed in: closes it and hands the session to
 * the tab that opened it. Any Teams app may open this page in its own sign-in
 * popup and would receive what is handed back, so the session goes only to the
 * portal's own Teams apps; anything else gets a failure.
 *
 * Handing off makes this window forget its session, so `handOff` runs only
 * after Teams has confirmed the popup. Outside Teams the SDK fails first and
 * nothing is forgotten: a link to this page can't sign anyone out.
 */
export async function finishTeamsSignIn(
  handOff: () => Promise<SessionTokens | null>
): Promise<void> {
  const teams = await loadTeams();
  const context = await teams.app.getContext();
  const openedBy = context.app.appId?.toString();
  if (!openedBy || !OWN_APP_IDS.has(openedBy)) {
    teams.authentication.notifyFailure("UnknownApp");
    return;
  }
  const tokens = await handOff();
  if (!tokens) {
    teams.authentication.notifyFailure("NotSignedIn");
    return;
  }
  teams.authentication.notifySuccess(JSON.stringify(tokens));
}
